import type { TMedicalTeamComment } from './medical-team-comments.type'
import MedicalTeamCommentListDivider from './medical-team-comment-divider.component'
import MedicalTeamCommentItem from './medical-team-comment-item.component'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import relativeTime from 'dayjs/plugin/relativeTime'
import { capitalizeFirstLetter } from 'src/utils/string-formatter.util'
import { useLocale } from 'src/hooks/use-locale'

export type TMedicalTeamCommentListProps = {
  medicalTeamCommentList: TMedicalTeamComment[]
  selectedComment?: { id: number; comment: string }
  onDeleteComment: (comment: TMedicalTeamComment) => void
  onSelectComment: ({ id, comment }: { id: number; comment: string }) => void
  canWrite?: boolean
  isPatientFollowupEnded?: boolean
}

export type TSortedGroupMedicalTeamComment = [string, TMedicalTeamComment[]]

const MedicalTeamCommentList = ({
  medicalTeamCommentList,
  selectedComment,
  onDeleteComment,
  onSelectComment,
  canWrite,
  isPatientFollowupEnded,
}: TMedicalTeamCommentListProps) => {
  const { locale } = useLocale()
  const { t } = useTranslation()
  dayjs.extend(relativeTime)
  dayjs.extend(updateLocale)

  const groupMedicalTeamCommentByDate = (medicalTeamCommentList: TMedicalTeamComment[]) => {
    const sortedMedicalTeamCommentList = [...medicalTeamCommentList].sort(
      (medTeamComment1, medTeamComment2) => {
        return new Date(medTeamComment2.updatedAt).getTime() - new Date(medTeamComment1.updatedAt).getTime()
      },
    )
    return sortedMedicalTeamCommentList.reduce((list, medicalTeamComment) => {
      const medicalTeamCommentDate = new Date(medicalTeamComment.updatedAt)
      const medicalTeamCommentCreatedDate = new Date(medicalTeamComment.createdAt)

      let groupLabel = medicalTeamCommentDate.toLocaleDateString(locale, {
        year: 'numeric',
      })

      const today = new Date(new Date().setHours(0, 0, 0, 0))
      const last30Days = new Date(new Date().setHours(0, 0, 0, 0))
      last30Days.setDate(last30Days.getDate() - 30)
      let completedDate = true
      if (medicalTeamCommentDate.getTime() > today.getTime()) {
        groupLabel = t('components.medical-team-comments.comment-today-date-group-label')
        completedDate = false
      } else if (medicalTeamCommentDate.getTime() > last30Days.getTime()) {
        groupLabel = t('components.medical-team-comments.comment-last-30-days-group-label')
        completedDate = false
      }
      const updatedDate = dayjs(medicalTeamCommentDate).format('D MMM YYYY HH:mm')
      const createdDate = dayjs(medicalTeamCommentCreatedDate).format('D MMM YYYY HH:mm')
      const updatedFromNow = completedDate ? updatedDate : dayjs(medicalTeamCommentDate).fromNow()
      const createdFromNow = completedDate ? createdDate : dayjs(medicalTeamCommentCreatedDate).fromNow()

      let dateLabel = capitalizeFirstLetter(updatedFromNow)
      let dateLabelTooltip = updatedDate
      if (updatedFromNow !== createdFromNow) {
        dateLabel = `${t('components.medical-team-comments.comment-today-date-label-updated', {
          updatedDateTime: updatedFromNow,
        })} ${t('components.medical-team-comments.comment-today-date-label-created', {
          createdDateTime: createdFromNow,
        })}`

        dateLabelTooltip = `${t('components.medical-team-comments.comment-today-date-label-updated', {
          updatedDateTime: updatedDate,
        })} ${t('components.medical-team-comments.comment-today-date-label-created', {
          createdDateTime: createdDate,
        })}`
      }
      medicalTeamComment.dateLabel = dateLabel
      medicalTeamComment.dateLabelTooltip = dateLabelTooltip

      const existingIndex = list.findIndex(([gl]) => gl === groupLabel)
      if (existingIndex < 0) {
        list.push([groupLabel, [medicalTeamComment]])
      } else {
        list[existingIndex]?.[1].push(medicalTeamComment)
      }
      return list
    }, [] as TSortedGroupMedicalTeamComment[])
  }

  const sortedGroupPatientMedEventByDate: TSortedGroupMedicalTeamComment[] =
    groupMedicalTeamCommentByDate(medicalTeamCommentList)

  if (sortedGroupPatientMedEventByDate.length === 0 && (!canWrite || isPatientFollowupEnded)) {
    return <p>{t('components.medical-team-comments.no-medical-team-comment')}</p>
  }

  return (
    <div className="c-w-full c-pl-2 c-flex c-flex-col c-items-center  ">
      {sortedGroupPatientMedEventByDate.map(([date, groupedMedTeamCommentList]) => (
        <div key={date} className="c-w-full c-flex c-flex-col c-items-center">
          <MedicalTeamCommentListDivider date={date} />
          {groupedMedTeamCommentList.map((medicalTeamComment) => (
            <MedicalTeamCommentItem
              key={medicalTeamComment.id}
              medicalTeamComment={medicalTeamComment}
              selectedComment={selectedComment}
              onDelete={onDeleteComment}
              onSelect={onSelectComment}
              canWrite={canWrite}
              isPatientFollowupEnded={isPatientFollowupEnded}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export default MedicalTeamCommentList
